import React, { useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { TabGroup } from "@/components/ui/TabGroup";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocationOptions } from "@/hooks/useLocationOptions";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import { useImobiliaria } from "@/hooks/useImobiliaria";
import { useTenant } from "@/hooks/useTenant";

interface SearchFilters {
  type: 'todos' | 'alugar' | 'vender';
  cidade: string;
  bairro: string;
  valorMax: string;
  quartos: string;
}

interface SearchBarProps {
  variant?: 'hero' | 'page' | 'compact';
  showTabs?: boolean;
  onSearch?: (filters: SearchFilters) => void;
  className?: string;
  defaultFilters?: Partial<SearchFilters>;
  showSearchButton?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  variant = 'hero',
  showTabs = true,
  onSearch,
  className,
  defaultFilters = {},
  showSearchButton = true
}) => {
  const isMobile = useIsMobile();
  const { subdominio } = useTenant();
  const { data: imobiliaria } = useImobiliaria(subdominio);
  const { cidades, getBairrosByCidade } = useLocationOptions(imobiliaria?.id);
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    type: 'todos',
    cidade: '',
    bairro: '',
    valorMax: '',
    quartos: '',
    ...defaultFilters
  });

  // Get bairros based on selected cidade
  const bairrosOptions = getBairrosByCidade(filters.cidade);

  const handleTabChange = (tab: 'todos' | 'alugar' | 'vender') => {
    const newFilters = { ...filters, type: tab };
    setFilters(newFilters);
  };

  const handleSearch = () => {
    onSearch?.(filters);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatCurrency = (value: string) => {
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    
    // Se não há números, retorna string vazia
    if (!numbers) return '';
    
    // Converte para número e formata
    const number = parseInt(numbers);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(number);
  };

  const updateFilter = (key: keyof SearchFilters, value: string) => {
    let formattedValue = value;
    
    // Formatar valor monetário para o campo valorMax
    if (key === 'valorMax') {
      formattedValue = formatCurrency(value);
    }
    
    const newFilters = { ...filters, [key]: formattedValue };
    setFilters(newFilters);
  };

  // Variant styles
  const containerStyles = {
    hero: "relative w-full max-w-[1102px] mx-auto px-4 lg:px-0",
    page: "flex flex-col lg:flex-row w-full min-h-[120px] lg:min-h-[140px] px-0 lg:px-0 py-0 lg:py-0 gap-4 lg:gap-6 lg:justify-between lg:items-end",
    compact: "w-full max-w-2xl mx-auto px-4"
  };

  const searchInputStyles = {
    hero: "flex flex-col sm:flex-row h-auto sm:h-20 lg:h-[88px] p-3 sm:p-4 lg:p-6 justify-center items-stretch gap-2 sm:gap-4 lg:gap-6 rounded-3xl bg-white shadow-[0_4px_16px_0_rgba(0,0,0,0.16)] overflow-hidden",
    page: "flex flex-col md:flex-row h-auto md:min-h-[72px] lg:min-h-[80px] p-0 justify-center items-stretch gap-0 rounded-2xl bg-white shadow-[0_4px_16px_0_rgba(0,0,0,0.16)] w-full overflow-hidden",
    compact: "flex flex-row h-12 p-2 px-4 justify-center items-center gap-2 rounded-full bg-white border border-gray-200 w-full"
  };

  const heroTabsStyles = "bg-neutral-gray-bg flex h-[56px] items-center rounded-tl-[8px] rounded-tr-[8px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.16)]";
  
  const heroInputStyles = "bg-white flex gap-4 h-[96px] items-center justify-center px-10 py-0 rounded-[16px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.16)]";

  const fieldStyles = {
    hero: "flex flex-col gap-1 px-4 py-3 justify-center w-full sm:w-auto border-b sm:border-b-0 sm:border-r border-gray-200 last:border-b-0 last:border-r-0",
    page: "flex w-full md:flex-1 px-4 py-4 md:py-0 md:px-4 lg:px-6 flex-col justify-center gap-1.5 md:min-h-[72px] lg:min-h-[80px] border-b md:border-b-0 md:border-r border-gray-200 last:border-b-0 last:border-r-0 cursor-text hover:bg-gray-50/50 transition-colors",
    compact: "flex flex-1 px-2 items-center gap-1 border-r border-gray-200 last:border-r-0"
  };

  const quartosFieldStyles = {
    hero: "flex flex-col gap-1 px-4 py-3 justify-center w-full sm:w-auto",
    page: "flex w-full md:w-auto md:flex-shrink-0 md:min-w-[120px] md:max-w-[140px] px-4 py-4 md:py-0 md:px-4 lg:px-6 flex-col justify-center gap-1.5 md:min-h-[72px] lg:min-h-[80px] border-b md:border-b-0 cursor-text hover:bg-gray-50/50 transition-colors",
    compact: "flex w-16 px-2 items-center gap-1"
  };

  const buttonStyles = {
    hero: "flex h-12 lg:h-14 px-6 lg:px-8 justify-center items-center gap-2 rounded-full bg-gray-text text-white text-base lg:text-xl w-full lg:w-auto",
    page: "flex h-10 lg:h-12 px-4 lg:px-6 justify-center items-center gap-2 rounded-full bg-gray-text text-white text-sm lg:text-base w-full lg:w-auto",
    compact: "flex h-10 px-4 justify-center items-center gap-1 rounded-full bg-gray-text text-white text-sm w-auto"
  };

  const tabs = [
    { id: 'todos', label: 'Todos' },
    { id: 'alugar', label: 'Alugar' },
    { id: 'vender', label: 'Vender' }
  ];

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMobileSearch = () => {
    handleSearch();
    setIsExpanded(false);
  };

  // Mobile unified version for hero variant
  if (isMobile && variant === 'hero') {
    return (
      <div className={cn("box-border flex flex-col items-center px-4 py-0 w-full", className)}>
        {/* Tabs compactos */}
        {showTabs && (
          <div className="bg-neutral-gray-bg flex h-10 items-center rounded-tl-[8px] rounded-tr-[8px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.16)] w-full max-w-[320px]">
            {tabs.map((tab, index) => (
              <div
                key={tab.id}
                className={cn(
                  "flex flex-col gap-1 h-full items-center justify-end flex-1 cursor-pointer",
                  index === 0 && "rounded-tl-[8px]",
                  index === tabs.length - 1 && "rounded-tr-[8px]",
                  filters.type === tab.id ? "bg-white" : ""
                )}
                onClick={() => handleTabChange(tab.id as 'todos' | 'alugar' | 'vender')}
              >
                <p className={cn(
                  "text-sm font-bold",
                  filters.type === tab.id ? "text-neutral-black-alt" : "text-neutral-gray-dark"
                )}>
                  {tab.label}
                </p>
                <div className={cn(
                  "h-[2px] w-full",
                  filters.type === tab.id ? "bg-brand-yellow" : "bg-neutral-gray-bg"
                )} />
              </div>
            ))}
          </div>
        )}

        {/* Campos em grid 2x2 - sempre visíveis */}
        <div className="bg-white flex flex-col gap-3 p-4 rounded-[16px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.16)] w-full max-w-[320px]">
          {/* Linha 1: Cidade e Bairro */}
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1 p-2.5 bg-gray-50 rounded-xl">
              <label className="text-xs font-semibold text-neutral-black-alt">Cidade</label>
              <LocationAutocomplete
                value={filters.cidade}
                onChange={(value) => updateFilter('cidade', value)}
                options={cidades}
                placeholder="Campinas"
                inputClassName="text-sm text-neutral-dark"
                onKeyPress={handleKeyPress}
              />
            </div>

            <div className="flex flex-col gap-1 p-2.5 bg-gray-50 rounded-xl">
              <label className="text-xs font-semibold text-neutral-black-alt">Bairro</label>
              <LocationAutocomplete
                value={filters.bairro}
                onChange={(value) => updateFilter('bairro', value)}
                options={bairrosOptions}
                placeholder="Taquaral"
                inputClassName="text-sm text-neutral-dark"
                onKeyPress={handleKeyPress}
              />
            </div>
          </div>

          {/* Linha 2: Valor e Quartos */}
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1 p-2.5 bg-gray-50 rounded-xl">
              <label className="text-xs font-semibold text-neutral-black-alt">Valor até</label>
              <input
                type="text"
                placeholder="R$ 2.800,00"
                value={filters.valorMax}
                onChange={(e) => updateFilter('valorMax', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full text-sm text-neutral-dark bg-transparent border-none outline-none placeholder:text-gray-400"
              />
            </div>

            <div className="flex flex-col gap-1 p-2.5 bg-gray-50 rounded-xl">
              <label className="text-xs font-semibold text-neutral-black-alt">Quartos</label>
              <input
                type="number"
                placeholder="2"
                value={filters.quartos}
                onChange={(e) => updateFilter('quartos', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full text-sm text-neutral-dark bg-transparent border-none outline-none placeholder:text-gray-400"
                min="0"
                max="10"
              />
            </div>
          </div>

          {/* Botão de busca - largura completa */}
          <button
            onClick={handleSearch}
            className="w-full bg-neutral-black text-white text-base font-medium py-3 rounded-full flex items-center justify-center gap-2 hover:bg-neutral-black/90 transition-colors"
          >
            Buscar imóveis
            <Search className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn(containerStyles[variant], className)}>
        {showTabs && (
          <div className="mb-4">
            <TabGroup
              tabs={tabs}
              activeTab={filters.type}
              onChange={handleTabChange}
              variant="rounded"
              size="sm"
            />
          </div>
        )}

        <div className={searchInputStyles[variant]}>
          <div className={cn(fieldStyles[variant])}>
            <LocationAutocomplete
              value={filters.cidade}
              onChange={(value) => updateFilter('cidade', value)}
              options={cidades}
              placeholder="Cidade"
              inputClassName="text-xs font-medium text-gray-600"
            />
          </div>

          <div className={cn(fieldStyles[variant])}>
            <LocationAutocomplete
              value={filters.bairro}
              onChange={(value) => updateFilter('bairro', value)}
              options={bairrosOptions}
              placeholder="Bairro"
              inputClassName="text-xs font-medium text-gray-600"
            />
          </div>

          <div className={cn(quartosFieldStyles[variant])}>
            <input
              type="number"
              placeholder="Quartos"
              value={filters.quartos}
              onChange={(e) => updateFilter('quartos', e.target.value)}
              className="w-full text-xs font-medium text-gray-600 bg-transparent border-none outline-none placeholder:text-gray-400"
              min="0"
              max="10"
            />
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={handleSearch}
            rightIcon={<Search className="w-4 h-4" />}
            className="ml-2"
          >
            <span className="sr-only">Buscar</span>
          </Button>
        </div>
      </div>
    );
  }

  // Hero variant has specific layout structure
  if (variant === 'hero') {
    return (
      <div className={cn(containerStyles[variant], className)}>
        <div className="flex flex-col items-center">
          {/* Custom Tabs for Hero */}
          {showTabs && (
            <div className={heroTabsStyles}>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={cn(
                    "flex flex-col gap-2 h-full items-center justify-end w-24 cursor-pointer",
                    tab.id === 'todos' && "rounded-tl-[8px]",
                    tab.id === 'vender' && "rounded-tr-[8px]",
                    filters.type === tab.id ? "bg-white" : ""
                  )}
                  onClick={() => handleTabChange(tab.id as 'todos' | 'alugar' | 'vender')}
                >
                  <p className={cn(
                    "text-sm font-bold leading-[26.6px]",
                    filters.type === tab.id ? "text-neutral-black-alt" : "text-neutral-gray-dark"
                  )}>
                    {tab.label}
                  </p>
                  <div className={cn(
                    "h-[2px] w-full",
                    filters.type === tab.id ? "bg-brand-gold" : "bg-neutral-gray-bg"
                  )} />
                </div>
              ))}
            </div>
          )}

          {/* Hero Input Section */}
          <div className={heroInputStyles}>
          {/* Cidade Field */}
          <div className="flex flex-col gap-1 items-start justify-center w-full lg:w-[200px] xl:w-[220px] px-4 border-r border-gray-200">
            <label className="text-sm font-bold text-neutral-black-alt leading-[26.6px]">Cidade</label>
            <LocationAutocomplete
              value={filters.cidade}
              onChange={(value) => updateFilter('cidade', value)}
              options={cidades}
              placeholder="Ex: Campinas"
              inputClassName="text-base font-normal text-neutral-dark"
              onKeyPress={handleKeyPress}
            />
          </div>

          {/* Bairro Field */}
          <div className="flex flex-col gap-1 items-start justify-center w-full lg:w-[200px] xl:w-[220px] px-4 border-r border-gray-200">
            <label className="text-sm font-bold text-neutral-black-alt leading-[26.6px]">Bairro</label>
            <LocationAutocomplete
              value={filters.bairro}
              onChange={(value) => updateFilter('bairro', value)}
              options={bairrosOptions}
              placeholder="Ex: Taquaral"
              inputClassName="text-sm font-normal text-neutral-dark leading-7"
              onKeyPress={handleKeyPress}
            />
          </div>

          {/* Valor Field */}
          <div className="flex flex-col gap-1 items-start justify-center w-full lg:w-[200px] xl:w-[220px] px-4 border-r border-gray-200">
            <label className="text-sm font-bold text-neutral-black-alt leading-[26.6px]">Valor até</label>
            <input
              type="text"
              placeholder="R$ 2.800,00"
              value={filters.valorMax}
              onChange={(e) => updateFilter('valorMax', e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full text-sm font-normal text-neutral-dark leading-7 bg-transparent border-none outline-none placeholder:text-gray-400"
            />
          </div>

          {/* Quartos Field */}
          <div className="flex flex-col gap-1 items-start justify-center w-full lg:w-[120px] xl:w-[140px] px-4">
            <label className="text-sm font-bold text-neutral-black-alt leading-[26.6px]">Quartos</label>
            <input
              type="number"
              placeholder="2"
              value={filters.quartos}
              onChange={(e) => updateFilter('quartos', e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full text-sm font-normal text-neutral-dark leading-7 bg-transparent border-none outline-none placeholder:text-gray-400"
              min="0"
              max="10"
            />
          </div>

          {/* Search Button */}
          <Button
            variant="secondary"
            size="lg"
            onClick={handleSearch}
            rightIcon={<Search className="w-6 h-6" />}
            className="bg-neutral-black hover:bg-neutral-black/90 h-[56px] px-6 lg:px-8 text-lg lg:text-xl whitespace-nowrap"
          >
            Buscar
          </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(containerStyles[variant], className)}>
      <div className="flex flex-col lg:flex-row items-center lg:items-end gap-6">
        <div className="flex flex-col items-center w-full lg:w-auto">
          {showTabs && (
            <TabGroup
              tabs={tabs}
              activeTab={filters.type}
              onChange={handleTabChange}
              variant="default"
              size="sm"
            />
          )}

          <div className={searchInputStyles[variant]}>
            {/* Cidade Field */}
            <div 
              className={cn(fieldStyles[variant], "group")}
            >
              {variant === 'page' && (
                <label className="text-xs md:text-sm font-semibold text-neutral-black/80 whitespace-nowrap pointer-events-none">
                  Cidade
                </label>
              )}
              <LocationAutocomplete
                value={filters.cidade}
                onChange={(value) => updateFilter('cidade', value)}
                options={cidades}
                placeholder={variant === 'page' ? "Ex: Campinas" : "Cidade"}
                inputClassName={cn(
                  "w-full font-normal text-neutral-black",
                  "text-base md:text-base lg:text-lg"
                )}
                onKeyPress={handleKeyPress}
              />
            </div>

            {/* Bairro Field */}
            <div 
              className={cn(fieldStyles[variant], "group")}
            >
              {variant === 'page' && (
                <label className="text-xs md:text-sm font-semibold text-neutral-black/80 whitespace-nowrap pointer-events-none">
                  Bairro
                </label>
              )}
              <LocationAutocomplete
                value={filters.bairro}
                onChange={(value) => updateFilter('bairro', value)}
                options={bairrosOptions}
                placeholder={variant === 'page' ? "Ex: Taquaral" : "Bairro"}
                inputClassName={cn(
                  "w-full font-normal text-neutral-black",
                  "text-base md:text-base lg:text-lg"
                )}
                onKeyPress={handleKeyPress}
              />
            </div>

            {/* Valor Field */}
            <div 
              className={cn(fieldStyles[variant], "group")}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                input?.focus();
              }}
            >
              {variant === 'page' && (
                <label className="text-xs md:text-sm font-semibold text-neutral-black/80 whitespace-nowrap pointer-events-none">
                  Valor até
                </label>
              )}
              <input
                type="text"
                placeholder={variant === 'page' ? "Ex: R$ 2.800" : "Valor máximo"}
                value={filters.valorMax}
                onChange={(e) => updateFilter('valorMax', e.target.value)}
                onKeyPress={handleKeyPress}
                className={cn(
                  "w-full font-normal text-neutral-black bg-transparent border-none outline-none placeholder:text-gray-400/70",
                  "text-base md:text-base lg:text-lg",
                  "py-1 focus:outline-none focus:ring-0"
                )}
              />
            </div>

            {/* Quartos Field */}
            <div 
              className={cn(quartosFieldStyles[variant], "group")}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                input?.focus();
              }}
            >
              {variant === 'page' && (
                <label className="text-xs md:text-sm font-semibold text-neutral-black/80 whitespace-nowrap pointer-events-none">
                  Quartos
                </label>
              )}
              <input
                type="number"
                placeholder={variant === 'page' ? "Ex: 2" : "Quartos"}
                value={filters.quartos}
                onChange={(e) => updateFilter('quartos', e.target.value)}
                onKeyPress={handleKeyPress}
                className={cn(
                  "w-full font-normal text-neutral-black bg-transparent border-none outline-none placeholder:text-gray-400/70",
                  "text-base md:text-base lg:text-lg",
                  "py-1 focus:outline-none focus:ring-0"
                )}
                min="0"
                max="10"
              />
            </div>

            {/* Search Button (optional) */}
        {showSearchButton && (
          <div className="flex items-center justify-center px-4 py-4 md:py-0 md:px-4 lg:px-6 w-full md:w-auto md:min-h-[72px] lg:min-h-[80px]">
            <Button
              variant="primary"
              size="default"
              onClick={handleSearch}
              rightIcon={<Search className="w-5 lg:w-6 h-5 lg:h-6" />}
              className="w-full md:w-auto h-12"
            >
              Buscar
            </Button>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { SearchBar, type SearchBarProps, type SearchFilters };
export default SearchBar;